import { delay, http, HttpResponse } from 'msw';

import { HostingAdvancedServerStatusMutationBody } from '@newfold/huapi-js';
import {
  getEmailSsoMockHandler,
  getHostingEmailListMockHandler,
  getSiteIpaddressListMockHandler,
  getSitesPerformanceScansListMockHandler,
  getSitesSshUserListV2MockHandler,
  getUserHostingMockHandler,
} from '@newfold/huapi-js/src/index.msw';
// import { getHostingUAPIMock } from '@newfold/huapi-js/src/index.msw';
import {
  DomainInfo200,
  HostingInfo200,
  HostingShellToggleBody,
  HostingSiteAddBody,
  HostingVisitors200,
  SitesAutoupdatesUpdateBody,
  SitesBackupsDetails200,
  SitesDomainsCreateBody,
  SiteUpdateBody,
} from '@newfold/huapi-js/src/index.schemas';

import { AccessLogsData } from './data/accessLogs';
import activityLogData from './data/activitylog';
import { getAiProductSitesMockResponse } from './data/ai-product-sites';
import { BackupsV2, metaDataV2 } from './data/backups';
import cronjobs from './data/cron-jobs';
import dbList from './data/dbList';
import directory from './data/directory-privacy';
import files from './data/docpath-selector';
import {
  addonDomainV2,
  domainsListV2,
  mainDomainActualV2,
} from './data/domains';
import { emailAccounts } from './data/emailAccounts';
import indexes from './data/folder-index-manager';
import ftpAccounts from './data/ftp';
import { getMockHostingById } from './data/hosting-account';
import { hostingAddons, spamExperts } from './data/hosting-addons';
import MOCK_HOSTING_ID_TO_TYPE from './data/hostingIdToType';
import { hostingList } from './data/hostingList';
import { osImages } from './data/imagesList';
import PhpErrorsData from './data/phpError';
import phpInfoHtmlContent from './data/phpInfo';
import { qualityMetricsInfo } from './data/quality-metrics';
import redirectList from './data/redirectsList';
import { accessLogsInfo, errorLogsInfo } from './data/site-logs';
import { siteSsoBuilderMulti, siteSsoWP } from './data/site-sso';
import { sitePerformanceScansMockHandler } from './data/sitePerformanceScans';
import {
  siteActive,
  sitesEmpty,
  sitesList,
  sitesListWithPendingCreate,
  sitesOneActive,
} from './data/sites';
import { ssheKeyInfo, sshInfo, sshKeysListData } from './data/ssh-keys';
import { visitorsResponse } from './data/visitors';

const isTestingEnv = process.env.NODE_ENV === 'test';
let pluginUpdateState = true;
let siteName: string | null = null;
let addSite: any = null;
let addDomain: DomainInfo200 | null = null;
let onboardingCompleted = true;
let serverStatus: string | null = null;
let delayTime = isTestingEnv ? 0 : 1000;
const errorStatusGET = '512';
const errorStatusMutate = '400';

let siteLockLicenseAttempt = 0;
// let hostingSitesRefetchCounter = 0;
type CodeguardAddonPlansType = {
  [key: number]: {
    plan: string;
    canUpgrade: boolean;
  };
};
const codeguardAddonPlans: CodeguardAddonPlansType = {
  1: { plan: 'Codeguard Basic', canUpgrade: true },
  4: { plan: 'Codeguard Enterprise', canUpgrade: false },
};

export const handlers = [
  // example of mocking a request for development
  // NOTE: it is best practice not to commit changes here, but rather to integrate
  // rest.get('*/hosting/:hostingid/sites', async () =>
  //   res(
  //     ctx.delay(1000),
  //     ctx.status(200, 'Mocked status'),
  //     ctx.json({ rows: [] }),
  //   ),
  // ),

  // GET site plugins
  http.get('*/v1/sites/:site_id/plugins', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        type: 'wordpress',
        auto_update: false,
        plugins: [
          {
            id: 'bluehost-wordpress-plugin',
            status: 'inactive',
            title: 'Bluehost',
            update: false,
          },
          {
            id: 'akismet',
            status: 'active',
            title: 'Akismet Anti-Spam',
            update: pluginUpdateState,
          },
          {
            id: 'wordpress-seo',
            status: 'active',
            title: 'Yoast SEO',
            update: false,
          },
          {
            id: 'woocommerce',
            status: 'active',
            title: 'WooCommerce',
            update: true,
            version: '6.7.0',
          },
        ],
      }),
      { status: 200 },
    );
  }),

  // POST, Update individual plugin
  http.post('*/v1/sites/:site/plugins/:plugin/update', async () => {
    pluginUpdateState = false;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 1,
      }),
      { status: 200 },
    );
  }),

  // POST, Update all plugins
  http.post('*/v1/sites/:site/plugins/update', async ({ params }) => {
    pluginUpdateState = false;
    const status = params.site === errorStatusMutate ? 400 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 1,
      }),
      { status },
    );
  }),

  // POST, Toggle plugin on/off
  http.post('*/v1/sites/:site/plugins/:plugin/toggle', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 1,
      }),
      { status: 200 },
    );
  }),

  // POST, Add Site //deprecated
  http.post('*/v1/hosting/:hostingId/sites', async ({ request }) => {
    const reqBody = (await request.json()) as HostingSiteAddBody;

    const status =
      reqBody?.title === 'error' ||
      reqBody?.title === 'urlInUse' ||
      (reqBody?.title === 'reassignDomain' && !reqBody?.force_vhost)
        ? Number(errorStatusMutate)
        : 200;

    type ResBodyType =
      | {
          site_id: number;
          status: string;
          success: boolean;
        }
      | { error: string };

    let resBody: ResBodyType = {
      site_id: 10,
      status: 'pending_create',
      success: true,
    };

    if (status === 200 && reqBody?.title) {
      const {
        title,
        domain,
        path,
        admin_email,
        datacenter,
        disk_limit_gib,
        workers,
      } = reqBody;

      addSite = {
        name: title,
        admin_email,
        datacenter,
        disk_limit_gib,
        workers,
        url: `${domain}/${path}`,
        id: 10,
        status: 'active',
        ssl: false,
        doc_root: '/public_html/site-uuid-or-something-10',
      };
    }

    if (reqBody?.title?.includes('urlInUse')) {
      resBody = {
        error: reqBody?.title,
      };
    }
    if (reqBody?.title?.includes('reassignDomain')) {
      resBody = {
        error: 'domainInUseForceVhostAvailable',
      };
    }

    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // POST, Add Site v2
  http.post('*/v2/hosting/:hostingId/sites', async ({ request }) => {
    const reqBody = (await request.json()) as HostingSiteAddBody;

    const status =
      reqBody?.title === 'error' ||
      reqBody?.title === 'urlInUse' ||
      (reqBody?.title === 'reassignDomain' && !reqBody?.force_vhost)
        ? Number(errorStatusMutate)
        : 200;

    type ResBodyType =
      | {
          site_id: number;
          status: string;
          success: boolean;
        }
      | { error: string };

    let resBody: ResBodyType = {
      site_id: 10,
      status: 'pending_create',
      success: true,
    };

    if (status === 200 && reqBody?.title) {
      const {
        title,
        domain,
        path,
        admin_email,
        datacenter,
        disk_limit_gib,
        workers,
      } = reqBody;

      addSite = {
        name: title,
        admin_email,
        datacenter,
        disk_limit_gib,
        workers,
        url: `${domain}/${path}`,
        id: 10,
        status: 'active',
        ssl: false,
        doc_root: '/public_html/site-uuid-or-something-10',
      };
    }

    if (reqBody?.title?.includes('urlInUse')) {
      resBody = {
        error: reqBody?.title,
      };
    }
    if (reqBody?.title?.includes('reassignDomain')) {
      resBody = {
        error: 'domainInUseForceVhostAvailable',
      };
    }

    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // PUT, Updating Values for a Site.
  http.put('*/v1/sites/:siteId', async ({ request }) => {
    const reqBody = (await request.json()) as SiteUpdateBody;

    const status =
      reqBody.title === 'error' || reqBody.title === 'urlInUse'
        ? Number(errorStatusMutate)
        : 200;

    type ResBodyType =
      | {
          success: boolean;
          sso: string;
        }
      | { error: string };

    let resBody: ResBodyType = {
      success: true,
      sso: 'https://a-link-to-wp-admin.com',
    };

    if (status === 200 && reqBody.title) {
      siteName = reqBody.title;
    }

    if (reqBody.title === 'urlInUse') {
      resBody = {
        error: 'urlInUse',
      };
    }

    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // DELETE, Allows deleting a site
  http.delete('*/v1/sites/:siteId', async ({ params }) => {
    const status = params.siteId === errorStatusMutate ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), { status });
  }),

  // DELETE, Allows deleting site content
  http.post('*/v1/sites/:siteId/reset', async ({ params }) => {
    const status = params.siteId === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        status: 'pending_reset',
        previous_state: 'active',
      }),
      { status },
    );
  }),

  // DELETE, Allows deleting a pending domain URL
  http.delete('*/v1/sites/:siteId/pending-domain', async ({ params }) => {
    const status = params.siteId === errorStatusMutate ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), { status });
  }),

  //GET, Sites list
  http.get('*/v1/hosting/:hostingId/sites', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const rows = sitesList;

    if (hostingId === 1) {
      rows.pop();
    }

    if (addSite) {
      rows.push(addSite);
    }

    const available_sites = hostingId === 200 ? 0 : 10;
    const items = hostingId === 202 ? sitesEmpty : rows;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        available_sites,
        max_sites: 2,
        limit: 10,
        n_pages: 1,
        n_rows: 1,
        page: 1,
        items,
      }),
      { status },
    );
  }),

  //GET, Sites list V2
  http.get('*/v2/hosting/:hostingId/sites', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const sites = () => {
      if (onboardingCompleted) return sitesListWithPendingCreate;

      // launch site setup flow if site is active
      return sitesOneActive;
      // return sitesOneCreating;

      // // show site installing modal
      // hostingSitesRefetchCounter += 1;
      // if (hostingSitesRefetchCounter < 3) return sitesOneCreating;
      // return sitesOneFailedCreate; // close modal when site becomes failed_create
      // return sitesOneActive; // launch site setup flow when site becomes active
    };
    const rows = sites();

    if (hostingId === 1) {
      rows.pop();
    }

    if (addSite) {
      const hasAddSite = rows.find(({ id }) => id === addSite?.id);
      !hasAddSite && rows.push(addSite);
    }

    const available_sites = hostingId === 200 ? 0 : 10;
    const items = hostingId === 202 ? sitesEmpty : rows;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        available_sites,
        max_sites: 2,
        limit: 10,
        total: items.length,
        n_pages: 1,
        n_rows: 1,
        page: 1,
        items,
      }),
      { status },
    );
  }),

  //GET, Hosting account - useHostingAccount
  // To see provisioning loading state:
  // change ctx.status to 403
  // change the Hosting status in host-app.ts to HostingStatus.PROVISIONING
  http.get('*/v1/hosting/:hostingId', async ({ params }) => {
    const hostingData = getMockHostingById(params.hostingId);

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(hostingData), { status: 200 });
  }),

  //GET, Hosting account info - useHostingAccountInfo
  http.get('*/v1/hosting/:hostingId/info', async ({ params }) => {
    const hostingInfo: HostingInfo200 = {
      ip: '17.25.10.34',
    };

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(hostingInfo), { status: 200 });
  }),

  //GET, Hosting onboarding info
  http.get('*/v1/hosting/:hostingId/onboarding', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({ completed: onboardingCompleted }),
      { status: 200 },
    );
  }),

  //PUT, Update Hosting onboarding info
  http.put('*/v1/hosting/:hostingId/onboarding', async () => {
    onboardingCompleted = true;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({ completed: onboardingCompleted }),
      { status: 200 },
    );
  }),

  //GET, Domains list by Hosting
  http.get('*/v1/hosting/:hostingId/domains', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const response = (() => {
      if (status === 512) return { errorMessage: 'error message' };
      if (status === 201) return { domains: [] };
      return {
        domains: [
          {
            id: 1,
            domain: 'testnewoctjbkjkl.com',
            ssl_issuer: 'Sectigo Limited',
            date_ssl_exp: '2099-01-02 23:59:59',
            status: 'active',
            ssl_status: 'Protected',
            autogen_id: null,
            type: 'main',
          },
          {
            id: 2,
            domain: 'website-33e387d0.lth.zqh.mybluehost.me',
            ssl_issuer: null,
            date_ssl_exp: null,
            status: 'active',
            ssl_status: 'Not protected',
            autogen_id: 1234,
          },
          {
            id: 3,
            domain: 'website-904cdc1a.lth.zqh.mybluehost.me',
            ssl_issuer: null,
            date_ssl_exp: null,
            status: 'active',
            ssl_status: 'Not protected',
            autogen_id: '1234',
          },
          {
            id: 4,
            domain: 'testnewoctjbkjkm.com',
            ssl_issuer: null,
            date_ssl_exp: '2099-01-02 23:59:59',
            status: 'active',
            ssl_status: 'Not protected',
          },
          {
            id: 5,
            domain: 'testnewoctjbkjkm.com',
            ssl_issuer: null,
            date_ssl_exp: '2099-01-02 23:59:59',
            status: 'active',
            ssl_status: 'Not protected',
            autogen_id: 0,
          },
          {
            id: 6,
            domain: 'testnewoctjbkjkn.com',
            ssl_issuer: null,
            date_ssl_exp: '2099-01-02 23:59:59',
            status: 'active',
            ssl_status: 'Not protected',
            autogen_id: '',
          },
          {
            id: 7,
            domain: 'testnewoctjbkjko.com',
            ssl_issuer: null,
            date_ssl_exp: '2099-01-02 23:59:59',
            status: 'active',
            ssl_status: 'Not protected',
            autogen_id: NaN,
          },
        ],
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //GET, DB_Limit
  http.get('*/v1/hosting/:hostingId/db/usage', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        db_limit: 20,
        db_count: 20,
      }),
      { status: 200 },
    );
  }),

  // GET, Site info
  http.get('*/v1/sites/:siteId', async ({ params }) => {
    const siteId = Number(params?.siteId);
    const site =
      sitesListWithPendingCreate.find(({ id }) => id === siteId) ||
      addSite ||
      siteActive;

    // for testing purposes we set id with the same siteId from the request
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        id: siteId,
        docroot: site?.docroot,
        path: site?.path || '',
        account_back_ref:
          site?.account_id || 'WN.test-different-site-backref-value',
        account_id: site?.account_id,
        uuid: '005d0702-076f-40a5-81dd-6142b66f9e9a',
        date_added: '2022-05-05 06:32:23',
        date_modified: '2022-06-22 06:41:52',
        name: siteName || site?.name || '', // NOTE: this is causing a bug in the site detail header
        type: site?.type || 'wordpress',
        detected_type: site?.detected_type || 'wordpress',
        subtype: site?.subtype || '',
        status: site?.status,
        url: site?.url,
        version: '6.0.1',
        domain: site?.domain,
        php_version: siteId === 1 ? 'PHP 8.0' : null,
        pending_url: site?.pending_url,
        pending_url_data: {
          domain_id: 1,
          domain: 'www.dougclarkfinewoodworking.com',
        },
        pagespeed: {
          lastrun: '2025-03-11 06:09:34',
          performance: '0.91',
          strategy: 'DESKTOP',
        },
        url_is_provisional: site?.url_is_provisional,
        is_primary: false,
        wp_multi_site: {
          enable: true,
          canonicalize_aliases: true,
        },
        resources: {
          storage: {
            available: 23,
            total: 25,
            unit: 'GB',
            used: 2,
          },
          workers: 0,
        },
        ...(!!site?.parent_site_id && { parent_site_id: site?.parent_site_id }),
      }),
      { status: 200 },
    );
  }),

  // GET addon Data
  http.get('*/v1/hosting/:hostingId/addons', async ({ request, params }) => {
    const url = new URL(request?.url);
    const type = url.searchParams.get('type');
    const id = Number(params.hostingId);
    const status = id === 512 ? 512 : 200;
    const addons = hostingAddons(type);

    let addonList = addons.filter((addon) => {
      // For test account 567567, return only one spamExperts addon
      if (type === 'spamexperts' && id === 567567) {
        return addon.id === spamExperts[0].id; // Only return the first addon
      }
      // For testing account 500, return only one backup addon
      // used to test the GET backup list error state
      if (type === 'backup' && (id === 500 || id === 404)) {
        return addon.id === String(id);
      }
      return addon;
    });

    if (id === 404 && type !== 'backup') {
      addonList = [];
    }

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ rows: addonList }), { status });
  }),

  // GET addon backup list
  http.get('*/v1/addons/:addon_id/backup/list', async ({ params }) => {
    const addonId = Number(params.addon_id);
    const status = [500, 512].includes(addonId) ? addonId : 200;

    const resBody = {
      home: [
        {
          backup_id: 'backup-1234',
          backup_status: {
            download_url: '',
            status: 'completed',
            tracking_id: 1234,
          },
          time_created: '',
        },
      ],
    };

    if (addonId === 404) {
      resBody.home = [];
    }

    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // GET Weebly SSO
  http.get('*/v1/addons/:id/weebly/sso', async ({ params }) => {
    const addonId = Number(params.id);
    const status = addonId === 512 ? addonId : 200;
    const sso = addonId === 404 ? null : `http://weebly-sso-${addonId}.com`;
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ sso }), { status });
  }),

  // POST call to fix the file ownership
  http.post('*/v1/hosting/:hosting_id/fixownership', async () => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        success: 1,
      }),
      { status: 200 },
    );
  }),

  // GET Webzai SSO
  http.get('*/v1/addons/:id/webzai/sso', async ({ params }) => {
    const addonId = Number(params.id);
    const status = addonId === 512 ? addonId : 200;
    const sso = addonId === 404 ? null : `http://webzai-sso-${addonId}.com`;
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ sso }), { status });
  }),

  // GET Backups plan_Info Data
  http.get('*/v1/addons/codeguard/:addon_id/plan', async ({ params }) => {
    const addonId = Number(params.addon_id);
    const planName = codeguardAddonPlans[addonId]?.plan;
    const canUpgrade = codeguardAddonPlans[addonId]?.canUpgrade ?? true;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        size_limit: 1073741824,
        plan_name: planName,
        back_reference: 'WN.CG.123',
        can_upgrade: canUpgrade,
        size: 130457600,
        status: 'active',
        seats_limit: 5,
        seats_count: 1,
        seats: [
          {
            url: 'http://rock219.eigdev.temp.domains/~tqsetti3',
            activated: true,
            website_id: 1,
          },
        ],
      }),
      { status: 200 },
    );
  }),

  // GET Codegurad SSO link
  http.get('*/addons/codeguard/:addon_id/login', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        link: 'https://www.staging.codeguard.com/websites?oauth_consumer_key=h2NUcZXfNACKUtq07cnS1xhIL2LStEmWUMf6kxfp&oauth_nonce=d1a339704f303851d4af153b3a1c2a0f&oauth_signature=P%2FTyWQa6jDQHm9%2F%2FRDO46XQYRsU%3D&oauth_signature_method=HMAC-SHA1&oauth_timestamp=1653664709&oauth_token=BPPb7ggO71EDw6lTACNbhnRq13JtIZhouagYJcEx&oauth_version=1.0',
      }),
      { status: 200 },
    );
  }),

  // GET Hosting SSO Link
  http.get('*/hosting/:hosting_id/sso', async ({ request }) => {
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('app');
    const status = queryParam === 'error' ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        url: queryParam === 'null' ? null : 'http://hosting-sso.com',
      }),
      { status },
    );
  }),

  // GET spamExperts SSO Link
  http.get('*/addons/:addonId/spamexperts/sso', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        url: 'https://spamlogin.com/?authticket=mock-se-auth-ticket',
      }),
      { status: 200 },
    );
  }),

  // GET WP SSO Link
  http.get('*/sites/:site_id/sso', async ({ request, params }) => {
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('bounce_name');
    const addonId = url.searchParams.get('addon_id');
    const status = queryParam === 'error' ? 512 : 200;
    const site =
      sitesListWithPendingCreate.find(
        ({ id }) => id === Number(params?.site_id),
      ) ||
      addSite ||
      siteActive;

    const resp =
      site?.type === 'builder' && site?.subtype === 'multi' && !addonId
        ? siteSsoBuilderMulti(site?.domain)
        : siteSsoWP;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(resp), { status });
  }),

  // GET Site Status
  http.get('*/v1/sites/:siteId/status', async ({ params }) => {
    const siteId = Number(params.siteId);
    const status = siteId === 512 ? 512 : 200;
    const response = (() => {
      if (siteId === 10)
        // show both steps
        return {
          cloudflare_ready: false,
          pending_url: 'http://newdomain.com',
          url: 'http://pki.mfe.mybluehost.me',
        };
      if (siteId === 11)
        // only show pending_url step, when cloudflare is installed
        return {
          cloudflare_ready: true,
          pending_url: 'http://newdomain.com',
          url: 'http://pki.mfe.mybluehost.me',
        };
      if (siteId === 12)
        // only show pending_url step, when cloudflare is NOT installed
        return {
          cloudflare_ready: undefined,
          pending_url: 'http://newdomain.com',
          url: 'http://pki.mfe.mybluehost.me',
        };
      if (siteId === 13)
        // only show cloudflare step
        return {
          cloudflare_ready: false,
          pending_url: undefined,
          url: 'http://pki.mfe.mybluehost.me',
        };
      if (siteId === 14)
        // do NOT show either step
        return {
          cloudflare_ready: undefined,
          pending_url: undefined,
          url: 'http://pki.mfe.mybluehost.me',
        };

      // do NOT show either step
      return {
        cloudflare_ready: true,
        pending_url: undefined,
        url: 'http://pki.mfe.mybluehost.me',
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status });
  }),

  // GET Codegurad backup list
  http.get('*/addons/codeguard/:addon_id/backups', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        backups: [
          {
            website_commit_id: 'ade4f8f39b28430f2fa3cd9d7f851ad3047109dc',
            event_time: '2022-06-22T10:14:02.687Z',
            database_backups: [
              {
                id: 20432,
                recommended: true,
                commit_id: 'afc5799b56df2584c55767376f4c50dd4458325f',
                event_time: '2022-05-20T12:47:26Z',
              },
            ],
          },
          {
            website_commit_id: 'ade4f8f39b28430f2fa3cd9d7f851ad3047109dc',
            event_time: '2022-06-02T10:14:02.687Z',
            database_backups: [
              {
                id: 20432,
                recommended: true,
                commit_id: 'afc5799b56df2584c55767376f4c50dd4458325f',
                event_time: '2022-05-20T12:47:26Z',
              },
            ],
          },
          {
            website_commit_id: 'afc5799b56df2584c55767376f4c50dd4458325f',
            event_time: '2022-05-20T12:47:26Z',
            database_backups: [
              {
                id: 20432,
                recommended: true,
                commit_id: 'ab79dfe82986637a62fff1d2774b810e2171d276',
                event_time: '2022-05-20T02:52:55Z',
              },
            ],
          },
          {
            website_commit_id: 'ab79dfe82986637a62fff1d2774b810e2171d276',
            event_time: '2022-05-20T02:52:55Z',
            database_backups: [
              {
                id: 20432,
                recommended: true,
                commit_id: '60a1dbceda52dbbc97a328a0878e063c69e146b9',
                event_time: '2022-05-19T19:40:05Z',
              },
              {
                id: 20432,
                recommended: false,
                commit_id: 'c67651f79593fcc788dc635dfd28bcaf2f3eec11',
                event_time: '2022-05-19T17:07:34Z',
              },
            ],
          },
          {
            website_commit_id: '5c79dfe82986637a62fff1d2774b810e2171c456',
            event_time: '2023-01-15T04:08:35Z',
            database_backups: [],
          },
          {
            website_commit_id: 'ab79dfe82986637a62fff1d277b810e2171c4561',
            event_time: '2023-06-03T02:52:55Z',
            database_backups: [
              {
                id: 20452,
                recommended: true,
                event_time: '2022-05-19T19:40:05Z',
              },
            ],
          },
        ],
      }),
      { status: 200 },
    );
  }),

  // POST, creation of a new backup for website
  http.post('*/addons/codeguard/:addon_id/backups', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ status: '1' }), { status: 200 });
  }),

  // POST, Restores the website backup
  http.post('*/addons/codeguard/:addon_id/backups/restore', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        website_success: true,
        database_success: true,
      }),
      { status: 200 },
    );
  }),

  // POST, Download ZIP of a backup
  http.post('*/addons/codeguard/:addon_id/backups/download', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ status: '1' }), { status: 200 });
  }),

  // GET Users Data
  http.get('*/v1/sites/:site_id/users', async ({ params }) => {
    const siteId = Number(params.site_id);
    const status = siteId === Number(errorStatusGET) ? 512 : 200;
    let users = [
      {
        display_name: 'Miles',
        id: 1,
        post_count: 0,
        role: 'administrator',
        user_email: 'miles@example.com',
        user_id: 1,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'James',
        id: 2,
        post_count: 0,
        role: 'administrator',
        user_email: 'james@example.com',
        user_id: 2,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'John Doe',
        id: 3,
        post_count: 0,
        role: 'contributor',
        user_email: 'john.doe@example.com',
        user_id: 3,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'Zeek Williamson',
        id: 4,
        post_count: 0,
        role: 'editor',
        user_email: 'zeek.williamson@gmail.com',
        user_id: 4,
        user_login: 'string',
        user_nicename: 'string',
      },
    ];
    if (siteId === 2) {
      users.push(
        {
          display_name: 'Juan Perez',
          id: 5,
          post_count: 0,
          role: 'contributor',
          user_email: 'juan.perez@example.com',
          user_id: 5,
          user_login: 'string',
          user_nicename: 'string',
        },
        {
          display_name: 'Luis Mendez',
          id: 6,
          post_count: 0,
          role: 'editor',
          user_email: 'luis.mendez@gmail.com',
          user_id: 6,
          user_login: 'string',
          user_nicename: 'string',
        },
      );
    }
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({ users: Number(errorStatusGET) === status ? [] : users }),
      { status },
    );
  }),

  // GET  V2 Users Data
  http.get('*/v2/sites/:site_id/users', async ({ request, params }) => {
    const siteId = Number(params.site_id);
    const status = siteId === Number(errorStatusGET) ? 512 : 200;
    const allUsers = [
      {
        display_name: 'Miles',
        id: 1,
        post_count: 0,
        role: 'administrator',
        user_email: 'miles@example.com',
        user_id: 1,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'James',
        id: 2,
        post_count: 0,
        role: 'administrator',
        user_email: 'james@example.com',
        user_id: 2,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'John Doe',
        id: 3,
        post_count: 0,
        role: 'contributor',
        user_email: 'john.doe@example.com',
        user_id: 3,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'Zeek Williamson',
        id: 4,
        post_count: 0,
        role: 'editor',
        user_email: 'zeek.williamson@gmail.com',
        user_id: 4,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'Juan Perez',
        id: 5,
        post_count: 0,
        role: 'contributor',
        user_email: 'juan.perez@example.com',
        user_id: 5,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'Luis Mendez',
        id: 6,
        post_count: 0,
        role: 'editor',
        user_email: 'luis.mendez@gmail.com',
        user_id: 6,
        user_login: 'string',
        user_nicename: 'string',
      },
      {
        display_name: 'test-user',
        id: 7,
        post_count: 0,
        role: 'administrator',
        user_email: 'test@example.com',
        user_id: 7,
        user_login: 'string',
        user_nicename: 'string',
      },
    ];
    // Extract pagination parameters from the request
    const url = new URL(request?.url);
    const page = Number(url.searchParams.get('page')) || 1; // Default to page 1
    const limit = Number(url.searchParams.get('limit')) || 5; // Default to 10 items per page

    // Calculate pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedUsers = allUsers.slice(startIndex, endIndex);
    // Prepare response
    const response = {
      total_users: allUsers.length,
      page,
      limit,
      users: paginatedUsers,
    };

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status });
  }),

  // GET WordPress Security SSL status
  http.get('*/v1/sites/:siteId/security/ssl/status', async ({ params }) => {
    //For unit test purposes, we assume siteId: 1 as active, otherwise inactive
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        message: 'acme',
        product: 'Basic SSL',
        provider: 'ssl-default',
        ssl_status:
          '1' === params.siteId
            ? 'active'
            : params.siteId === '2'
            ? 'in_progress'
            : 'inactive',
        domain: 'shopwave.com',
        upgradeable: '1' === params.siteId,
      }),
      { status: 200 },
    );
  }),

  // GET Backup status
  http.get('*/v1/sites/:siteId/backups/check', async ({ params }) => {
    //For unit test purposes, we assume siteId: 1 as active, otherwise inactive
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        has_backups: '1' === params.siteId,
      }),
      { status: 200 },
    );
  }),

  // GET details about the codeguard and jetpack
  http.get('*/v1/sites/:siteId/backups/details', async ({ params }) => {
    type RespType = SitesBackupsDetails200;
    let resp: RespType = {
      // jetpack and codeguard available
      codeguard: {
        addon_id: '1',
        // @ts-expect-error Check huapi schema
        status: 'active',
        plan_name: 'Codeguard Basic',
        seats_available: 1,
        seats_count: 1,
        website_id: '1234',
        database_id: '1234',
        activated: true,
        url: 'test324324234.com/',
      },
      jetpack: { plugin_exists: true, addon_exists: true },
    };

    if (params.siteId === '2') resp = {}; // no backup product

    if (params.siteId === '4')
      // redeem points for codeguard
      resp = {
        codeguard: {
          addon_id: '12345',
          // @ts-expect-error Check huapi schema
          status: 'active',
          plan_name: 'Codeguard Basic',
          seats_available: 2,
          seats_count: 1,
          website_id: '4',
          database_id: '',
          activated: false,
          url: '',
        },
      };

    if (params.siteId === '5')
      // for jetpack backup product active
      resp = {
        jetpack: {
          addon_id: '123456',
          plugin_exists: true,
          addon_exists: true,
          has_active_addon: true,
          addon_back_reference: 'WN.HP.000000000',
          can_upgrade: true,
          has_active_plugin: true,
          last_backup_date: '2024-07-24 19:32:19',
          next_url: null,
          pending_upgrade: false,
          plan_name: 'Jetpack Backup Premium',
          status: 'active',
          can_purchase: false,
        },
      };
    if (params.siteId === '6')
      // for jetpack backup plugin is not active
      resp = {
        jetpack: {
          addon_id: '123456',
          plugin_exists: true,
          addon_exists: true,
          has_active_addon: true,
          addon_back_reference: 'WN.HP.000000000',
          can_upgrade: true,
          has_active_plugin: false,
          last_backup_date: '2024-07-24 19:32:19',
          next_url: null,
          pending_upgrade: false,
          plan_name: 'Jetpack Backup Premium',
          status: 'active',
          can_purchase: false,
        },
      };

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(resp), { status: 200 });
  }),

  //POST, add codeGuard to the site
  http.post('*/v1/sites/:siteId/backups/codeguard', async ({ params }) => {
    const status = params.siteId === errorStatusMutate ? 400 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        website_id: 4782820,
        database_id: 2217362,
        success: 1,
      }),
      { status },
    );
  }),

  // POST update security SSL status
  http.post('*/v1/sites/:siteId/security/ssl/acme', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 0,
        message:
          'Your SSL information is temporarily unavailable. Please check back later.',
      }),
      { status: 200 },
    );
  }),

  // GET enforce https status
  http.get('*/v1/sites/:siteId/security/https', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 1,
        redirection_status: 0,
        message:
          'There is trouble fetching your https status. Please try again',
      }),
      { status: 200 },
    );
  }),

  // POST update enforce https status
  http.post('*/v1/sites/:siteId/security/https', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: 1,
        reredirection_status: 1,
        message: '',
      }),
      { status: 200 },
    );
  }),

  // GET check core files
  http.get('*/v1/sites/:site_id/security/core/check', async ({ params }) => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: params.site_id === '1',
        results:
          'Success: WordPress installation verifies against checksums.\n',
      }),
      { status: 200 },
    );
  }),

  // POST reset core files
  http.post('*/v1/sites/:site_id/security/core/reset', async ({ params }) => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: params.site_id === '2',
        results:
          'Downloading WordPress 6.0 (en_US)...\nmd5 hash verified: 1df60aaa07dede05de4d6d69d2234e19\nSuccess: WordPress downloaded.\n',
      }),
      { status: 200 },
    );
  }),

  //GET sitelock addon details
  http.get('*/v1/sites/:site_id/security/sitelock', async ({ params }) => {
    const returnLite = params.site_id === '1';
    const returnCDN = params.site_id === '123';
    if (siteLockLicenseAttempt < 5 && returnLite) {
      siteLockLicenseAttempt++;

      await delay(delayTime);

      return new HttpResponse(
        JSON.stringify({
          success: 1,
          addon_id: '12345',
          plan: 'Lite',
          platform_id: 'WN.SL.123',
          billing_code: 'SITELOCK_LITE',
          upgrade_levels: ['Essential'],
          has_cdn: returnCDN,
        }),
        { status: 200 },
      );
    }

    // after 5 tries, change to essential, if we are not looking for CDN
    // mock to test interval in CTB
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: params.site_id !== '11' ? 1 : 0,
        addon_id: '12345',
        plan: returnCDN ? 'Prevent' : 'Essential',
        platform_id: 'WN.SL.234',
        billing_code: returnCDN ? 'SITELOCK_PREVENT' : 'SITELOCK_ESSENTIAL',
        upgrade_levels: returnCDN
          ? ['SITELOCK_PREVENT_PLUS']
          : ['SITELOCK_PREVENT', 'SITELOCK_PREVENT_PLUS'],
        has_cdn: returnCDN,
      }),
      { status: 200 },
    );
  }),

  //GET sitelock addon details
  http.get('*/v2/sites/:site_id/security/sitelock', async ({ params }) => {
    const returnLite = params.site_id === '1';
    const returnCDN = params.site_id === '123';

    await delay(delayTime);
    if (params.site_id === '404' && siteLockLicenseAttempt < 5) {
      siteLockLicenseAttempt++;

      return HttpResponse.json(
        {
          error: 'sitelockNotFound',
        },
        { status: 404 },
      );
    }

    if (siteLockLicenseAttempt < 5 && returnLite && params.site_id !== '404') {
      siteLockLicenseAttempt++;

      return new HttpResponse(
        JSON.stringify({
          success: 1,
          addon_id: '12345',
          plan: 'Lite',
          platform_id: 'WN.SL.123',
          billing_code: 'SITELOCK_LITE',
          upgrade_levels: ['Essential'],
          has_cdn: returnCDN,
          source: 'hal',
        }),
        { status: 200 },
      );
    }

    // after 5 tries, change to essential, if we are not looking for CDN
    // mock to test interval in CTB
    if (params.site_id !== '404') {
      return new HttpResponse(
        JSON.stringify({
          success: params.site_id !== '11' ? 1 : 0,
          addon_id: '12345',
          plan: returnCDN ? 'Prevent' : 'Essential',
          platform_id: 'WN.SL.234',
          billing_code: returnCDN ? 'SITELOCK_PREVENT' : 'SITELOCK_ESSENTIAL',
          upgrade_levels: returnCDN
            ? ['SITELOCK_PREVENT_PLUS']
            : ['SITELOCK_PREVENT', 'SITELOCK_PREVENT_PLUS'],
          has_cdn: returnCDN,
          source: 'hal',
        }),
        { status: 200 },
      );
    }

    if (params.site_id === '404') {
      return HttpResponse.json(
        {
          error: 'sitelockNotFound',
        },
        { status: 404 },
      );
    }
  }),

  //GET sitelock SSO
  http.get('*/v1/addons/sitelock/:addon_id/sso', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        site_url: 'https://www.bluehost.com',
        sitelock_id: '15312463',
        url: 'https://www.bluehost.com',
      }),
      { status: 200 },
    );
  }),

  //GET sitelock SSO
  http.get('*/v2/addons/sitelock/:addon_id/sso', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        site_url: 'https://www.bluehost.com',
        sitelock_id: '15312463',
        url: 'https://www.bluehost.com',
      }),
      { status: 200 },
    );
  }),

  //GET eCommerce
  http.get('*/v1/hosting/:hosting_id/ecommerce', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        addon_id: 914,
        level: 'basic',
        status: 'active',
        subtype: null,
        type: 'ecommerce',
        back_ref: 'WN.HP.123',
      }),
      { status: 200 },
    );
  }),

  // GET Disk usage details
  http.get('*/v1/hosting/:hosting_id/info/diskusage', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        diskused: '46GB',
        disklimit: '50GB', //'Unlimited',
        filesused: 1000,
        fileslimit: 11000,
      }),
      { status: 200 },
    );
  }),

  //GET list of available PHP versions
  http.get('*/v1/sites/:site_id/settings/php-versions', async ({ params }) => {
    const status = params.site_id === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        versions: ['PHP 8.1', 'PHP 8.2', 'PHP 8.3'],
        latest: 'PHP 8.3',
        deprecated: [
          'PHP 5.5',
          'PHP 5.6',
          'PHP 7.0',
          'PHP 7.1',
          'PHP 7.2',
          'PHP 7.3',
          'PHP 7.4',
          'PHP 8.0',
        ],
      }),
      { status },
    );
  }),

  // POST update PHP version
  http.post('*/v1/sites/:site_id/settings/php-version', async ({ params }) => {
    const status = params.site_id === errorStatusMutate ? 400 : 200;
    const nestedResult = {
      success: 0,
      site: 'http://rock219.eigdev.temp.domains/~tqtwoze8',
      nested_sites: [
        'http://rock219.eigdev.temp.domains/~tqtwoze8/testing',
        'https://www.bluehost.com',
        'https://www.bluehost.com/domains',
        'https://www.bluehost.com/hosting',
        'https://www.bluehost.com/hosting/shared',
        'https://www.bluehost.com/hosting/dedicated',
        'https://www.bluehost.com/hosting/vps',
      ],
    };
    const result = params.site_id === '1' ? { success: 1 } : nestedResult;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(result), { status });
  }),

  // GET site auto updates status
  http.get('*/v1/sites/:site_id/autoupdates', async ({ params }) => {
    const status = params.site_id === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        core: false,
        plugins: false,
        themes: false,
      }),
      { status },
    );
  }),

  // PUT Automatic updates for WordPress
  http.put('*/v1/sites/:site_id/autoupdates', async ({ request, params }) => {
    const status = params.site_id === errorStatusMutate ? 400 : 200;
    const reqBody = (await request.json()) as SitesAutoupdatesUpdateBody;
    const { type, enabled } = reqBody;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        core: type === 'core' ? enabled : null,
        plugins: type === 'plugins' ? enabled : null,
        themes: type === 'themes' ? enabled : null,
      }),
      { status },
    );
  }),

  // GET debug mode status
  http.get('*/v1/sites/:site_id/settings/debug', async ({ params }) => {
    const status = params.site_id === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ status: true }), { status });
  }),

  // POST update debug mode
  http.post('*/v1/sites/:site_id/settings/debug', async ({ params }) => {
    const status = params.site_id === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: 1 }), { status });
  }),

  // GET coming soon page status
  http.get('*/v1/sites/:site_id/settings/coming-soon', async ({ params }) => {
    const status = params.site_id === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ enable: true }), { status });
  }),

  // POST update coming soon page
  http.post('*/v1/sites/:site_id/settings/coming-soon', async ({ params }) => {
    const status = params.site_id === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: 1 }), { status });
  }),
  //GET Speed page INFO
  // GET, Site info

  // getOptions: (domain, siteId, options) =>
  // axios.get(`${API_USER}/hosting/${domain}/wordpress/${siteId}/options`, {
  //   params: { options },
  //   paramsSerializer: params => queryStringify(params, { arrayFormat: 'comma' }),
  //   camelCase: true,
  // }),

  http.get(
    '*/v1/sites/:site_id/performance/endurance_cache_level',
    async () => {
      await delay(delayTime);

      return new HttpResponse(JSON.stringify({ endurance_cache_level: 3 }), {
        status: 200,
      });
    },
  ),

  //PUT for update endurance cache level
  http.put('*/v1/sites/:site_id/performance/endurance_page_cache', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        status: 'ok',
        type: 'page',
        result: 'Success',
      }),
      {
        status: 200,
      },
    );
  }),

  //POST for options of cache level selection
  http.post(
    '*/v1/sites/:site_id/performance/endurance_cache_level',
    async () => {
      await delay(delayTime);

      return new HttpResponse(JSON.stringify({ endurance_cache_level: 0 }), {
        status: 200,
      });
    },
  ),

  //remove cloudflare
  http.delete('*/v1/addons/:addon_id/cloudflare', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: true,
        status: 'string',
      }),
      {
        status: 200,
      },
    );
  }),

  //install cloudflare
  http.post('*/v1/addons/:addon_id/cloudflare', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        status: 'success',
        addon_id: 288,
      }),
      {
        status: 200,
      },
    );
  }),

  // GET for site Info
  http.get('*/v1/wordpress/:domain/sites/:siteId', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        constants: {
          EMPTY_TRASH_DAYS: '7',
          WP_AUTO_UPDATE_CORE: 'true',
          WP_POST_REVISIONS: '5',
        },
        domain: 'sndyoast18.net',
        options: {
          admin_email: 'narasimhadinesh.somisetty@web.com',
          allow_major_auto_core_updates: true,
          allow_minor_auto_core_updates: 1,
          allow_toggle_auto_core_updates: true,
          auto_update_core_major: 'enabled',
          auto_update_plugin: true,
          auto_update_plugins: {
            'akismet/akismet.php': 1,
            'bluehost-wordpress-plugin/bluehost-wordpress-plugin.php': 1,
            'creative-mail-by-constant-contact/creative-mail-plugin.php': 1,
            'google-analytics-for-wordpress/googleanalytics.php': 1,
            'hello.php': 1,
            'jetpack/jetpack.php': 1,
            'optinmonster/optin-monster-wp-api.php': 1,
            'woocommerce/woocommerce.php': 1,
            'wpforms-lite/wpforms.php': 1,
          },
          auto_update_theme: true,
          auto_update_themes: {
            sinatra: 1,
            twentytwenty: 1,
            twentytwentyone: 1,
            twentytwentytwo: 1,
          },
          blogdescription: '',
          blogname: 'Welcome',
          close_comments_days_old: '28',
          close_comments_for_old_posts: true,
          comments_per_page: '20',
          endurance_cache_level: '1',
          home: 'http://rock427.eigdev.temp.domains/~sndyoat6',
          jetpack_options: {
            fallback_no_verify_ssl_certs: 0,
            id: 206270972,
            last_heartbeat: 1659451529,
            old_version: '11.1.2:1658319918',
            public: 1,
            recommendations_banner_enabled: '1',
            time_diff: 0,
            version: '11.2:1659451535',
          },
          jetpack_private_options: {
            blog_token:
              'DKRnpWMuLk99Crj&0itehGi525oDS(m8.Mq2GPX&XgcD4zUoZYmMB^HB8H$jYxmmq',
          },
          jetpack_protect_blocked_attempts: null,
          jetpack_wga: { code: null },
          mm_brand: 'BlueHost',
          mm_coming_soon: true,
          mm_host: 'BlueHost',
          siteurl: 'http://rock427.eigdev.temp.domains/~sndyoat6',
          staging_relationships: {},
          stylesheet: 'twentytwentytwo',
          template: 'twentytwentytwo',
          woocommerce_bacs_settings: {},
          woocommerce_cheque_settings: {},
          woocommerce_cod_settings: {},
          woocommerce_currency: 'USD',
          woocommerce_default_country: 'US:CA',
          woocommerce_dimension_unit: 'cm',
          woocommerce_eway_payments_settings: {},
          woocommerce_flat_rate_1_settings: {},
          woocommerce_flat_rate_2_settings: {},
          woocommerce_klarna_checkout_settings: {},
          woocommerce_klarna_payments_settings: {},
          woocommerce_ppec_paypal_settings: {},
          woocommerce_product_type: '',
          woocommerce_sell_in_person: '',
          woocommerce_square_settings: {},
          woocommerce_store_address: '',
          woocommerce_store_address_2: '',
          woocommerce_store_city: '',
          woocommerce_store_postcode: '',
          woocommerce_stripe_settings: {},
          woocommerce_weight_unit: 'kg',
          woowizard_country: '',
        },
        path: 'public_html',
        plugins: [
          {
            id: 'bluehost-wordpress-plugin',
            status: 'active',
            title: 'Bluehost',
            update: 'none',
            update_version: null,
            version: '2.11.7',
          },
          {
            id: 'google-analytics-for-wordpress',
            status: 'active',
            title: 'Google Analytics for WordPress by MonsterInsights',
            update: 'none',
            update_version: '8.7.0',
            version: '8.7.0',
          },
          {
            id: 'akismet',
            status: 'inactive',
            title: 'Akismet Anti-Spam',
            update: 'none',
            update_version: '5.0',
            version: '5.0',
          },
          {
            id: 'optinmonster',
            status: 'active',
            title: 'OptinMonster',
            update: 'available',
            update_version: '2.9.0',
            version: '2.8.1',
          },
          {
            id: 'creative-mail-by-constant-contact',
            status: 'active',
            title: 'Creative Mail by Constant Contact',
            update: 'none',
            update_version: '1.5.3',
            version: '1.5.3',
          },
          {
            id: 'woocommerce',
            status: 'active',
            title: 'WooCommerce',
            update: 'available',
            update_version: '6.8.0',
            version: '6.7.0',
          },
          {
            id: 'jetpack',
            status: 'active',
            title: 'Jetpack',
            update: 'none',
            update_version: '11.2',
            version: '11.2',
          },
          {
            id: 'wpforms-lite',
            status: 'active',
            title: 'WPForms Lite',
            update: 'none',
            update_version: '1.7.5.5',
            version: '1.7.5.5',
          },
        ],
        site_id: '2f7075626c69635f68746d6c2f',
        themes: [
          {
            id: 'sinatra',
            name: 'sinatra',
            screenshot: {
              file: 'screenshot.jpg',
              url: 'https://i0.wp.com/themes.svn.wordpress.org/sinatra/1.2.1/screenshot.jpg',
            },
            status: 'inactive',
            title: 'Sinatra',
            update: 'none',
            update_version: null,
            version: '1.2.1',
          },
          {
            id: 'twentytwenty',
            name: 'twentytwenty',
            screenshot: {
              file: 'screenshot.png',
              url: 'https://i0.wp.com/themes.svn.wordpress.org/twentytwenty/2.0/screenshot.png',
            },
            status: 'inactive',
            title: 'Twenty Twenty',
            update: 'none',
            update_version: null,
            version: '2.0',
          },
          {
            id: 'twentytwentyone',
            name: 'twentytwentyone',
            screenshot: {
              file: 'screenshot.png',
              url: 'https://i0.wp.com/themes.svn.wordpress.org/twentytwentyone/1.6/screenshot.png',
            },
            status: 'inactive',
            title: 'Twenty Twenty-One',
            update: 'none',
            update_version: null,
            version: '1.6',
          },
          {
            id: 'twentytwentytwo',
            name: 'twentytwentytwo',
            screenshot: {
              file: 'screenshot.png',
              url: 'https://i0.wp.com/themes.svn.wordpress.org/twentytwentytwo/1.2/screenshot.png',
            },
            status: 'active',
            title: 'Twenty Twenty-Two',
            update: 'none',
            update_version: null,
            version: '1.2',
          },
        ],
        update: 'none',
        users: [
          {
            display_name: 'narasimhadinesh.somisetty',
            id: 1,
            meta: { maestro_web_pro: null, maestro_wpp_identifier: null },
            post_count: 8,
            role: 'administrator',
            user_email: 'narasimhadinesh.somisetty@web.com',
            user_id: 1,
            user_login: 'narasimhadinesh.somisetty',
            user_nicename: 'narasimhadinesh-somisetty',
          },
        ],
        wp_version: '6.0.1',
      }),
      {
        status: 200,
      },
    );
  }),
  // POST update enforce clear all cache status
  http.post('*/v1/sites/:site_id/performance/cachepurge', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), { status: 200 });
  }),

  // Get Cloudflare status
  http.get('*/v1/user/:userId/cloudflare-cdn/:domain', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ status: 'active' }), {
      status: 200,
    });
  }),

  // Get cloudflarecdn_premium status
  http.get('*/v1/users/:userId/cdn/:domain', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({ activeCdn: 'cloudflarecdn_premium' }),
      { status: 200 },
    );
  }),

  // GET addon Data
  http.get('*/v1/hosting/:hostingId/cloudfare/addons', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        rows: [
          {
            id: 228,
            type: 'cloudfare',
            subtype: true,
            status: 'active',
          },
        ],
      }),
      { status: 200 },
    );
  }),

  // GET SSH Shell Info
  http.get('*/v1/hosting/:hostingId/shell', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ enabled: false }), { status });
  }),

  // PUT SSH Shell Info
  http.put('*/v1/hosting/:hostingId/shell', async ({ request, params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    const { enabled } = (await request.json()) as HostingShellToggleBody;
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ enabled }), { status });
  }),

  //GET SSH Cred Data
  http.get('*/v1/hosting/:hostingId/ssh', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        ...sshInfo,
      }),
      { status },
    );
  }),

  //GET SSH Key
  http.get('*/v1/hosting/:hostingId/ssh/fetch', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        ...ssheKeyInfo,
      }),
      { status: 200 },
    );
  }),

  //DELETE SSH Keys List
  http.delete('*/v2/hosting/:hostingId/ssh/keys', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        deleted: 1,
        key_name: 'id_rsa.pub',
      }),
      { status: 200 },
    );
  }),

  //POST Add SSH Key
  http.post('*/v3/hosting/:hostingId/ssh/import', async () => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        public_key_name: 'id_rsa11.pub',
      }),
      { status: 200 },
    );
  }),

  //GET SSH Keys List
  http.get('*/v4/hosting/:hosting_id/ssh/keys', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(sshKeysListData), {
      status,
    });
  }),

  //POST, Add ftp
  http.post('*/v1/hosting/:hosting_id/ftp', async ({ request, params }) => {
    const reqBody = (await request.json()) as Record<string, string>;
    const hostingId = Number(params.hosting_id);
    const status = isTestingEnv ? hostingId : 200;
    const httpStatus = status === 400 || status === 512 ? status : 200;

    await delay(delayTime);

    if (httpStatus === 400) {
      return new HttpResponse(JSON.stringify({ error: 'ftpUserExists' }), {
        status: 400,
      });
    }

    return new HttpResponse(
      JSON.stringify({
        user: reqBody?.user || 'test11@aze.lcb.mybluehost.me',
        pass:
          reqBody?.pass || 'dnzEdj!ptxm2cN@xj?G3oweuvU3>qxcneFybje4jcqw?uHmhtq',
      }),
      { status: httpStatus },
    );
  }),

  //PUT, Update ftp
  http.put('*/v1/hosting/:hosting_id/ftp', async ({ request, params }) => {
    const reqBody = (await request.json()) as Record<string, string>;
    const hostingId = Number(params.hosting_id);
    const status = isTestingEnv ? hostingId : 200;
    const responseDelay = status === 201 ? 200 : delayTime;
    const httpStatus = status === 400 || status === 512 ? status : 200;

    await delay(responseDelay);

    if (httpStatus !== 200) {
      return new HttpResponse(JSON.stringify({ error: 'failed' }), {
        status: httpStatus,
      });
    }

    return new HttpResponse(
      JSON.stringify({
        status: 1,
        user: reqBody?.user,
        domain: reqBody?.domain,
      }),
      { status: 200 },
    );
  }),

  //GET, Ftp Password Score
  http.get(
    '*/v1/hosting/:hostingId/password/score',
    async ({ request, params }) => {
      const url = new URL(request.url);
      const queryParam = url.searchParams.get('password');
      const status = queryParam === 'undefined' ? 512 : 200;
      await delay(delayTime);
      return new HttpResponse(JSON.stringify({ score: 84 }), { status });
    },
  ),

  //GET, Ftp generate password
  http.get('*/v1/hosting/:hostingId/password/generate', async () => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({ password: 'testgeneratedpassword' }),
      {
        status: 200,
      },
    );
  }),

  //GET, Cron Jobs List
  http.get('*/v1/hosting/:hosting_id/crontab', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify(cronjobs), {
      status: 200,
    });
  }),

  //POST, Add Cron Job
  http.post('*/v1/hosting/:hosting_id/crontab', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ key: 4067492046 }), {
      status: 200,
    });
  }),

  //PUT, Edit Cron Job
  http.put('*/v1/hosting/:hosting_id/crontab', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ key: 4067492046 }), {
      status: 200,
    });
  }),

  //DELETE, Delete Cron Job
  http.delete('*/v1/hosting/:hosting_id/crontab', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), {
      status: 200,
    });
  }),

  //GET, View Last run
  http.get('*/v1/hosting/:hostingId/crontab/last-run', async ({ request }) => {
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('command');
    const status = queryParam === 'undefined' ? 512 : 200;
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({ last_run: '2025-06-03 10:36:02' }),
      { status },
    );
  }),

  //GET, List of databases name
  http.get('*/v2/hosting/:hosting_id/db', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    return new HttpResponse(JSON.stringify(dbList), {
      status,
    });
  }),

  //GET, List of database users
  http.get('*/v1/hosting/:hostingId/db/users', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        users: [
          {
            dblist: ['azelcbmy_WP1SQ'],
            shortuser: 'WP1SQ',
            user: 'azelcbmy_WP1SQ',
          },
        ],
      }),
      {
        status,
      },
    );
  }),

  //POST Repair Database
  http.post('*/v1/hosting/:hostingId/db/repair', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        tables: [
          {
            table: 'Hml_actionscheduler_actions',
            status:
              "note     : The storage engine for the table doesn't support repair",
          },
          {
            table: 'Hml_actionscheduler_claims',
            status:
              "note     : The storage engine for the table doesn't support repair",
          },
          {
            table: 'Hml_actionscheduler_groups',
            status:
              "note     : The storage engine for the table doesn't support repair",
          },
        ],
      }),
      {
        status,
      },
    );
  }),

  //POST Check Database
  http.post('*/v1/hosting/:hostingId/db/check', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        tables: [
          {
            table: 'OBH_actionscheduler_actions',
            status: 'OK',
          },
          {
            table: 'OBH_actionscheduler_claims',
            status: 'OK',
          },
          {
            table: 'OBH_actionscheduler_groups',
            status: 'OK',
          },
        ],
      }),
      {
        status,
      },
    );
  }),

  //Activity logs
  http.get('*/v1/hosting/:hosting_id/activity-log', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(activityLogData), {
      status: 200,
    });
  }),

  //Add redirects
  http.post('*/v1/hosting/:hostingId/redirects', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: 1 }), {
      status: 200,
    });
  }),

  //GET redirects List
  http.get('*/v2/hosting/:hostingId/redirects', async ({ params }) => {
    const status = params.hostingId === '333';
    await delay(delayTime);

    return new HttpResponse(JSON.stringify(status ? [] : redirectList), {
      status: 200,
    });
  }),

  // GET hotlinkprotection config details
  http.get(
    '*/v1/hosting/:hostingId/hotlinkconfig',
    async ({ request, params }) => {
      const url = new URL(request.url);
      const queryParam = url.searchParams.get('docpath');
      const status = queryParam === 'undefined' ? 512 : 200;
      await delay(delayTime);
      return new HttpResponse(
        JSON.stringify({
          direct_request: 1,
          file_types: ['pdf', 'exe', 'jpg', 'html'],
          protocol_type: 'http',
          redirect_url: 'www.xyz.com',
        }),
        { status },
      );
    },
  ),

  //Add hotlinkprotection config details
  http.post('*/v1/hosting/:hostingId/hotlinkconfig', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify({}), {
      status: 200,
    });
  }),

  //GET Visitor Info

  http.get('*/v1/hosting/:hosting_id/visitors', async () => {
    await delay(delayTime);

    const resp: HostingVisitors200 = visitorsResponse;

    return new HttpResponse(JSON.stringify(resp), { status: 200 });
  }),

  //DELETE Delete Redirect
  http.delete('*/v1/hosting/:hostingId/redirects', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: 1 }), {
      status: 200,
    });
  }),

  //GET access web mail SSO Link
  getEmailSsoMockHandler({
    url: 'https://box2252.bluehost.com:2096/cpsess8210899391/webmail/jupiter/index.html?login=1&post_login=77763594879137',
  }),

  // Get email accounts list
  getHostingEmailListMockHandler(emailAccounts),

  //POST, Create Email account
  http.post('*/v1/hosting/:hostingId/emails', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify({ address: 'testemail@test.com' }), {
      status: 200,
    });
  }),

  //DELETE, Delete email account
  http.delete('*/v1/hosting/:hosting_id/emails', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify({}), {
      status: 200,
    });
  }),

  //Put change password
  http.put('*/v1/hosting/:hostingId/emails', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify({}), {
      status: 200,
    });
  }),

  //POST, Create Php info
  http.post('*/v1/sites/:site_id/php-info', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify(phpInfoHtmlContent), {
      status: 200,
    });
  }),

  //GET Advanced Server Info
  http.get('*/v1/hosting/:hostingId/advanced/server', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    const resBody = {
      status: serverStatus === null ? 'active' : serverStatus,
      uptime: '4 days',
      uptime_seconds: 411313,
      actions: {
        reboot: 0,
        rescue: 1,
        start: 1,
        stop: 0,
        unrescue: 0,
      },
    };
    if (status >= 400) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), {
      status,
    });
  }),

  //PUT Advanced Server Info
  http.put(
    '*/v1/hosting/:hostingId/advanced/server',
    async ({ request, params }) => {
      const status = params.site_id === errorStatusMutate ? 400 : 200;
      const { action } =
        (await request.json()) as HostingAdvancedServerStatusMutationBody;

      if (action === 'stop') {
        serverStatus = 'stopped';
      }
      if (action === 'start' || action === 'reboot') {
        serverStatus = 'active';
      }

      await delay(delayTime);

      return new HttpResponse(
        JSON.stringify({
          status:
            action === 'stop'
              ? 'pending_stop'
              : action === 'start'
              ? 'pending_start'
              : action === 'reboot'
              ? 'pending_soft_reboot'
              : 'undefined',
          uptime: '4 days',
          uptime_seconds: 411313,
          actions: {
            reboot: 0,
            rescue: 0,
            start: 0,
            stop: 0,
            unrescue: 0,
          },
        }),
        {
          status,
        },
      );
    },
  ),

  //GET Advanced Server Capabilities
  http.get('*/v1/hosting/:hostingId/capabilities', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const hostingUpgrade = hostingId === 999 ? false : true;
    const status = hostingId === Number(errorStatusGET) ? 512 : 200;

    let data;
    if (hostingId in MOCK_HOSTING_ID_TO_TYPE) {
      data = MOCK_HOSTING_ID_TO_TYPE[hostingId].capabilities;
    } else {
      data = {
        //default capabilities.
        sites: true,
        cpanel: true,
        whm: false,
        server_controls: false,
        advanced_hosting: false,
        reset_password: false,
        hosting_upgrade: hostingUpgrade,
        cbs_backups: true,
      };
    }

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(data), { status });
  }),

  //GET reseller whm SSO
  http.get('*v1/hosting/:hostingId/reseller/sso/whm', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        url: 'http://reseller-whm-sso.com',
      }),
      { status: 200 },
    );
  }),

  //GET whm SSO
  http.get('*/v1/hosting/:hostingId/advanced/sso_whm', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        url: 'http://vps-whm-sso.com',
        ssl_setup: {
          required: 1,
          server_ip: '1.1.1.1',
          dns_updated: 0,
          dns_zone_with_us: 0,
          server_hostname: null,
          domain: null,
        },
      }),
      { status: 200 },
    );
  }),

  //GET system console SSO
  http.get('*/v1/hosting/:hostingId/advanced/console', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    if (status !== 200) {
      return HttpResponse.json(
        {
          error: 'systemConsoleSsoError',
          details: 'Unable to generate system console SSO link at this time.',
        },
        { status },
      );
    }

    return new HttpResponse(
      JSON.stringify({
        url: 'http://vps-systemconsole-sso.com',
      }),
      { status: 200 },
    );
  }),

  //GET Name Servers
  http.get('*/v1/hosting/:hostingId/nameservers', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        status: 'enabled',
        success: true,
        nameservers: ['brand.nameserver.01', 'brand.nameserver.02'],
      }),
      { status },
    );
  }),

  //GET Name Servers v2
  http.get('*/v2/hosting/:hostingId/nameservers', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        id: '2',
        is_default: null,
        brand: 'bluehost',
        hosts: [
          {
            host: 'devns1.websitewelcome.com',
            ips: ['162.240.197.99'],
          },
          {
            host: 'devns2.websitewelcome.com',
            ips: ['162.240.197.99'],
          },
        ],
      }),
      { status },
    );
  }),

  //GET WP Site Version Data
  http.get('*/v1/sites/:siteId/version', async ({ params }) => {
    const update = params.siteId === '1';
    const status = params.siteId === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        update,
        current: '6.2',
      }),
      { status },
    );
  }),

  //GET Name Servers(DNS) Data
  http.get('*/v1/sites/:siteId/dns', async ({ params }) => {
    const status = params.siteId === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: true,
        records: [
          {
            type: 'NS',
            value: 'ns1.temp.domains.com',
            name: 'u012345.com.',
          },
          {
            type: 'NS',
            value: 'ns2.temp.domains.com',
            name: 'u012345.com.',
          },
        ],
      }),
      { status },
    );
  }),

  //GET jetpack addon and plugin status
  http.get('*/v1/sites/:siteId/backups/jetpack', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        has_active_plugin: false,
        has_active_addon: false,
      }),
      { status: 200 },
    );
  }),

  //GET, Domains list by Site v2
  http.get('*/v2/sites/:siteId/domains', async ({ params }) => {
    const siteId = isTestingEnv ? params.siteId : 1;
    const response = (() => {
      if (siteId === '512') return { errorMessage: 'error message' };
      if (siteId === '404')
        return { total: 0, page: 1, limit: 1000, domains: [] };

      if (addDomain) {
        const hasAddDomain = domainsListV2.find(
          ({ id }) => id === addDomain?.id,
        );
        !hasAddDomain && domainsListV2.push(addDomain);
      }

      return {
        total: 10,
        page: 1,
        limit: 1000,
        domains: domainsListV2,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //GET, Domains list by Hosting v2
  http.get('*/v2/hosting/:hostingId/domains', async ({ params }) => {
    const hostingId = isTestingEnv ? params.hostingId : 1;
    const response = (() => {
      if (hostingId === '512') return { errorMessage: 'error message' };
      if (hostingId === '404')
        return { total: 0, page: 1, limit: 1000, domains: [] };

      if (addDomain) {
        const hasAddDomain = domainsListV2.find(
          ({ id }) => id === addDomain?.id,
        );
        !hasAddDomain && domainsListV2.push(addDomain);
      }

      return {
        total: 10,
        page: 1,
        limit: 1000,
        domains: domainsListV2,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //POST, Unassign domain from hosting account
  http.post('*/v1/hosting/:hostingId/domains/unassign', async ({ params }) => {
    const hostingId = isTestingEnv ? params.hostingId : 1;
    const response = (() => {
      if (hostingId === '409') return { errorMessage: 'domainUnassignFailed' };
      return { queued: true };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  // Get the domain availability
  http.get(
    '*/v1/hosting/:hosting_id/domain/availability',
    async ({ params }) => {
      await delay(delayTime);

      return new HttpResponse(
        JSON.stringify({
          registered: true,
          assignable: true,
          assigned: false,
          can_assign: true,
          assigned_to_this_product: false,
        }),
        { status: 200 },
      );
    },
  ),

  // Post the domain assign status
  http.post('*/v1/hosting/:hosting_id/domain/assign', async ({ params }) => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), { status: 200 });
  }),

  // GET domain dns status
  http.get('*/v1/sites/:siteId/domain', async ({ params }) => {
    const status = params.siteId === errorStatusGET ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        nameserver_match: false,
        a_match: false,
        connected: '3' === params.siteId,
        detected: {
          a: ['162.241.218.31'],
          ns: [
            'ns-cloud-e1.googledomains.com',
            'ns-cloud-e2.googledomains.com',
            'ns-cloud-e3.googledomains.com',
            'ns-cloud-e4.googledomains.com',
          ],
        },
        wanted: {
          a: ['50.87.245.167'],
          ns: ['ns1.betabh.com', 'ns2.betabh.com'],
        },
      }),
      { status },
    );
  }),

  // POST add an existing external domain to site
  http.post('*/v1/sites/:siteId/domain', async ({ params }) => {
    const status = params.siteId === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    return new HttpResponse(JSON.stringify({ success: true }), { status });
  }),

  // NOTE: removed so we can see the real screenshot behavior in mock
  //GET Thumbnail data
  http.get('*/v1/thumbnail/get-screenshot', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        thumbnail_loc:
          'https://maestrotopic-qa.s3.amazonaws.com/thumbnails/bhanu-test/f129c276-53ab-4448-a6e7-396ed2cf54ec.jpeg',
        msg: 'Thumbnail already exists',
      }),
      { status: 200 },
    );
  }),

  // GET Site Migration failed case
  http.get('*/v1/hosting/:hostingId/migrations', async ({ params }) => {
    const status = params.hostingId === errorStatusGET ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        rows: [
          {
            migration_id: 12345,
            prospect_url: 'https://testwpsrc11apr2023.com/',
            temp_url: 'https://tempurl.com/',
            status: 'migration_failed',
          },
          {
            migration_id: 12346,
            prospect_url: 'https://testwpsrc11apr2023.com/',
            temp_url: 'https://tempurl.com/',
            status: 'in_progress',
            name: 'Migration TEST',
          },
          {
            migration_id: 12347,
            prospect_url: 'https://testwpsrc11apr2023.com/',
            temp_url: 'https://tempurl.com/',
            status: 'requires_intervention',
          },
          {
            migration_id: 12348,
            prospect_url: 'https://testwpsrc11apr2023.com/',
            temp_url: 'https://tempurl.com/',
            status: 'in_progress',
          },
        ],
      }),
      { status },
    );
  }),

  // GET Site Migration site_scan status
  http.get(
    '*/v1/hosting/:hostingId/migrations/site-scan',
    async ({ request }) => {
      const url = new URL(request.url);
      const queryParam = url.searchParams.get('prospect_url');
      const status = queryParam === 'error' ? 512 : 200;

      await delay(delayTime);

      return new HttpResponse(
        JSON.stringify({
          transferable: true,
          callback_url:
            'https://testwpdest11apr2023.com/wp-admin/plugin-install.php?tab=plugin-information&plugin=bluehost-site-migrator',
        }),
        { status },
      );
    },
  ),

  // POST Migrate an existing site
  http.post('*/v1/hosting/:hostingId/migrations', async ({ params }) => {
    const status = params.hostingId === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        success: true,
      }),
      { status },
    );
  }),

  http.get('*/v1/hosting/:hostingId/migrations/info', async ({ request }) => {
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('migration_id');
    const status = queryParam === 'error' ? 512 : 200;

    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        migration_id: '876453284765',
        domain: 'domain1.com',
        site_url: 'testwpdest11apr2023.com/test',
        hostname: 'box5401.bluehost.com',
        feasible: '1',
        source_type: 'internal',
        product_type: 'wordpress',
        incoming_domain: 'testwpdest11apr2023.com',
        is_valid_migration_id: false,
      }),
      { status },
    );
  }),

  //GET, list of databases
  http.get('*/v1/hosting/:siteID/db', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        databases: [
          {
            db: 'arlpljte_WPQ67',
            sitename: 'Welcome',
            size: 4165992.448,
            sizemeg: '3.97',
            usercount: 7,
            userlist: [
              {
                db: 'arlpljte_WPQ67',
                user: 'arlpljte_WPQ67',
              },
              {
                db: 'arlpljte_WPQ67',
                user: 'arlpljte_tools_pma',
              },
            ],
          },
          {
            db: 'arlpljte_WPQ87',
            sitename: 'Welcome',
            size: 4165992.448,
            sizemeg: '3.48',
            usercount: 7,
            userlist: [
              {
                db: 'arlpljte_WPQ67',
                user: 'arlpljte_WPQ67',
              },
              {
                db: 'arlpljte_WPQ67',
                user: 'arlpljte_tools_pma',
              },
            ],
          },
        ],
      }),
      { status: 200 },
    );
  }),

  //GET, list of users
  http.get('*/v1/hosting/:siteID/db/users', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        users: [
          {
            dblist: ['arlpljte_WPQ67'],
            shortuser: 'WPQ67',
            user: 'arlpljte_WPQ67',
          },
          {
            dblist: ['arlpljte_WPyBs'],
            shortuser: 'WPyBs',
            user: 'arlpljte_WPyBs',
          },
          {
            dblist: [],
            shortuser: 'sampletest1235',
            user: 'arlpljte_sampletest1235',
          },
        ],
      }),
      { status: 200 },
    );
  }),

  // GET Sites for all hosting accounts
  http.get('*/v2/sites', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        limit: 1000,
        page: 1,
        total: 115,
        items: sitesList,
      }),
      { status: 200 },
    );
  }),

  // GET Sites for all hosting accounts with pagination and search
  http.get('*/v3/sites', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        limit: 1000,
        page: 1,
        pages: 2,
        total: 100,
        items: sitesList,
      }),
      { status: 200 },
    );
  }),

  http.get('*/v1/user/sites/ai', async ({ request }) => {
    await delay(delayTime);

    return HttpResponse.json(getAiProductSitesMockResponse(request.url));
  }),

  // GET all hosting accounts info
  http.get('*/v1/account/hosting', async () => {
    let status = 200;
    const resBody = {
      accounts: [
        {
          account_id: '123456789',
          feature_set: 'hosting_10s',
          hosting_id: '1000',
          platform: 'jarvis',
          prod_inst_id: 'WN.HP.622649385',
          status: 'active',
          current_sites: 1,
          max_sites: '3',
        },
        {
          account_id: '987654321',
          feature_set: 'hosting_10s',
          hosting_id: '2000',
          platform: 'jarvis',
          prod_inst_id: 'WN.HP.622650716',
          status: 'active',
          current_sites: 1,
          max_sites: '1',
        },
        {
          account_id: '5000',
          feature_set: 'hosting_10s',
          hosting_id: '5000',
          platform: 'jarvis',
          prod_inst_id: 'WN.HP.622650717',
          status: 'decom',
          current_sites: 1,
          max_sites: '1',
        },
        {
          account_id: '987654321',
          feature_set: 'hosting_10s',
          hosting_id: '8000',
          platform: 'jarvis',
          prod_inst_id: 'WN.HP.622650716',
          status: 'active',
          current_sites: 1,
          max_sites: '1',
        },
      ],
    };
    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // GET all hosting accounts events
  http.get('*/v1/account/events', async () => {
    const accountEvents = {
      events: [
        {
          hosting_id: 1,
          addon_id: null,
          count: 1,
          date_first: '2024-09-01 00:00:00',
          date_last: '2024-09-01 00:00:00',
          domain_id: null,
          event: 'pending_url example.com added',
          site_id: 1,
          tenant_id: 1,
        },
        {
          hosting_id: 1,
          addon_id: null,
          count: 3,
          date_first: '2024-09-01 00:00:05',
          date_last: '2024-09-01 00:01:30',
          domain_id: 1,
          event: 'ok dns',
          site_id: null,
          tenant_id: 1,
        },
      ],
      utc: '2024-09-01 00:01:30',
    };

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(accountEvents), { status: 200 });
  }),

  // POST, Add Domains
  http.post('*/v1/sites/:siteId/domains', async ({ request }) => {
    const reqBody = (await request.json()) as SitesDomainsCreateBody;
    const hasReassignDomain =
      reqBody?.domain && reqBody?.domain.includes('reassignDomain');

    const status =
      reqBody.domain === 'error' || (hasReassignDomain && !reqBody?.force_vhost)
        ? Number(errorStatusMutate)
        : 200;

    type ResBodyType =
      | {
          id: number;
          status: string;
          success: boolean;
        }
      | { error: string };

    let resBody: ResBodyType = {
      id: 10,
      status: 'pending_create',
      success: true,
    };

    if (status === 200 && reqBody.domain) {
      addDomain = addonDomainV2;
      addDomain.domain = reqBody?.domain;
    }

    if (hasReassignDomain) {
      resBody = {
        error: 'domainInUseForceVhostAvailable',
      };
    }

    await delay(delayTime);

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // GET, list of site access logs
  http.get('*/v1/sites/:siteId/logs/access', async ({ params }) => {
    const status = Number(params.siteId) || 200;
    const resBody = accessLogsInfo[status] || {};

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // GET, list of site error logs
  http.get('*/v1/sites/:siteId/logs/error', async ({ params }) => {
    const status = Number(params.siteId) || 200;
    const resBody = errorLogsInfo[status] || {};

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  // TODO: add mock for zone editor url
  http.get('*/v1/domains/:domainId/zoneeditor-url', async ({ params }) => {
    const status = Number(params.siteId) || 200;
    const resBody = {
      // url: '', // user does not have permissions
      url: 'https://www.cpanel.com', // cpanel
      // url: 'https://www.google.com', // account manager
    };

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  //GET, Domain Info
  http.get('*/v1/domains/:domainId', async ({ params }) => {
    const domainId = isTestingEnv ? params.domainId : 1;
    const response = (() => {
      if (domainId === '512') return { errorMessage: 'error message' };
      if (domainId === '2') return mainDomainActualV2;

      return addonDomainV2;
    })();
    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //GET, FTP account list by Hosting
  http.get('*/v1/hosting/:hostingId/ftp', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const response = (() => {
      if (status === 512) return { errorMessage: 'error message' };
      if (status === 201)
        return {
          ftp_accounts: [],
          special_ftp_accounts: [],
          server_name: 'ifw.xze.mybluehost.me',
        };
      return {
        ...ftpAccounts,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //GET, Directory Privacy list by Hosting
  http.get('*/v1/hosting/:hostingId/directoryprivacy', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const response = (() => {
      if (status === 512) return { errorMessage: 'error message' };
      if (status === 201)
        return {
          directory: [],
        };
      return {
        directory,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  // POST add Directory Privacy
  http.post('*/v1/hosting/:hostingId/directoryprivacy', async (request) => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        docpath: '/home/test/public_html',
        action: 'enable',
        htuser: 'testing123',
        pass: 'Password1!',
      }),
      { status: 200 },
    );
  }),

  // PUT Directory Privacy
  http.put('*/v1/hosting/:hostingId/directoryprivacy', async (request) => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        docpath: '/home/test/public_html',
        action: 'enable',
        htuser: 'testing1234',
        pass: 'Password1!',
      }),
      { status: 200 },
    );
  }),

  // delete Directory Privacy
  http.delete('*/v1/hosting/:hostingId/directoryprivacy', async (request) => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        file: '/home/test/public_html',
        status: 'inherited',
      }),
      { status: 200 },
    );
  }),

  // delete Directory Privacy particular user
  http.delete(
    '*/v1/hosting/:hostingId/directoryprivacy/user',
    async (request) => {
      await delay(delayTime);
      return new HttpResponse(
        JSON.stringify({
          status: 1,
        }),
        { status: 200 },
      );
    },
  ),

  //GET, Folder Index manager indexes list by Hosting
  http.get('*/v1/hosting/:hostingId/index', async ({ params }) => {
    const hostingId = Number(params.hostingId);
    const status = isTestingEnv ? hostingId : 200;
    const response = (() => {
      if (status === 512) return { errorMessage: 'error message' };
      if (status === 201)
        return {
          indexes: [],
        };
      return {
        indexes,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  //GET, Folder list by Hosting
  http.get('*/v2/hosting/:hostingId/files', async ({ request, params }) => {
    const hostingId = Number(params.hostingId);
    let status = isTestingEnv ? hostingId : 200;
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('path');
    status = queryParam === 'error' ? 512 : 200;

    const response = (() => {
      if (status === 512) return { errorMessage: 'error message' };
      if (status === 201)
        return {
          files: [],
        };
      return {
        files,
      };
    })();

    await delay(delayTime);

    return new HttpResponse(JSON.stringify(response), { status: 200 });
  }),

  // GET Hosting SSO Link
  http.get('*/hosting/:hosting_id/sso', async ({ request }) => {
    const url = new URL(request.url);
    const queryParam = url.searchParams.get('app');
    const status = queryParam === 'error' ? 512 : 200;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        url: queryParam === 'null' ? null : 'http://hosting-sso.com',
      }),
      { status },
    );
  }),

  // GET backup list info
  http.get('*/v2/addons/:addon_id/backup/list', async () => {
    await delay(delayTime);

    return new HttpResponse(JSON.stringify(BackupsV2));
  }),

  // POST backups restore
  http.post('*/v2/addons/:addon_id/backup/restore', async (request) => {
    // const reqBody = (await request.json()) as BackupRestoreV2Body
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        tracking_id: 123456,
        message: 'Your request has been accepted and is being processed.',
      }),
      { status: 200 },
    );
  }),

  // POST backups donwload for File and Database
  http.post('*/v2/addons/:addon_id/backup/download', async (request) => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        tracking_id: 123456,
        message: 'Job queued',
      }),
      { status: 200 },
    );
  }),

  http.get('*/v2/addons/:addon_id/backup/status', async () => {
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        backup_download_url: 'https://server.com/home_file.tar.gz',
        status: 'completed',
        progress_percentage: 50,
      }),
    );
  }),

  http.get('*/v1/addons/:addon_id/backup/meta', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(metaDataV2));
  }),

  http.get('*/v1/addons/:addon_id/backup', async ({ params }) => {
    const status = params.addon_id === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    const resBody = {
      download_url:
        params?.addon_id === '404'
          ? 'Backup_Not_found'
          : 'https://example.com/backup.zip',
      message: 'https://example.com/backup.zip',
      status: 'completed',
    };

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  http.post('*/v1/addons/:addon_id/backup', async ({ request, params }) => {
    const status = params.addon_id === errorStatusMutate ? 400 : 200;

    await delay(delayTime);

    const resBody = {
      message: 'https://example.com/backup.zip',
      tracking_id: 123456,
    };

    if (status !== 200) return HttpResponse.json(resBody, { status });
    return new HttpResponse(JSON.stringify(resBody), { status });
  }),

  http.get('*/v1/sites/:siteId/quality-metrics', async ({ params }) => {
    await delay(delayTime);
    const resBody = qualityMetricsInfo[200];
    return new HttpResponse(JSON.stringify(resBody), { status: 200 });
  }),

  /** GET /v1/sites/:siteId/performance/scans — Insights Lighthouse chart + scan picker */
  getSitesPerformanceScansListMockHandler(() => {
    const scans = sitePerformanceScansMockHandler.createYearSpanningMockScans();
    return {
      data: scans,
      meta: {
        page: 1,
        per_page: 10,
        total: scans.length,
      },
    };
  }),

  // GET DNS records
  http.get(
    '*/hosting/:hosting_id/advanced/dns',
    async ({ request, params }) => {
      const hostingId = Number(params.hostingId);
      const url = new URL(request.url);
      const hostNameParam = url.searchParams.get('hostname');
      const domainNameParam = url.searchParams.get('domain');
      const status = hostingId && hostNameParam && domainNameParam ? 200 : 400;
      await delay(delayTime);

      return new HttpResponse(
        JSON.stringify({
          dns: [
            {
              content: '1.1.1.1',
              name: 'test.bluehost.com',
              ttl: 14400,
              type: 'A',
            },
          ],
        }),
        { status },
      );
    },
  ),

  // POST, Update DNS record
  http.post('*/v1/hosting/:hosting_id/advanced/dns', async () => {
    pluginUpdateState = false;
    await delay(delayTime);

    return new HttpResponse(
      JSON.stringify({
        dns_exists: false,
        changed: 0,
      }),
      { status: 200 },
    );
  }),

  // POST, promote staging site -- Atomic only
  http.post('*/v1/sites/:siteId/staging', async ({ request }) => {
    // TODO: account for other responses
    return new HttpResponse(
      JSON.stringify({
        job_id: '123456789',
      }),
      { status: 200 },
    );
  }),

  // Access logs
  http.get('*/v1/domains/:domainId/logs/access', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(AccessLogsData), {
      status: 200,
    });
  }),
  // php errors
  http.get('*/v1/hosting/:hosting_id/php-errors', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(PhpErrorsData), { status: 200 });
  }),

  // GET wp solution licenses
  http.get('*/v1/hosting/:accountId/product/license-details', async () => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        wpsolution: {
          creator: [
            {
              prodInstId: 'WN.HP.628441437',
            },
          ],
          commerce: [],
        },
      }),
      { status: 200 },
    );
  }),

  // GET re-image imageOS list
  http.get('*/v1/hosting/:hostingId/advanced/images', async () => {
    await delay(delayTime);
    return new HttpResponse(JSON.stringify(osImages), { status: 200 });
  }),

  // POST backups restore for File and Database, full website
  http.post('*/v3/addons/:addon_id/backup/site_restore', async (request) => {
    await delay(delayTime);
    return new HttpResponse(
      JSON.stringify({
        tracking_id: 123456,
        message: 'Job queued',
      }),
      { status: 200 },
    );
  }),

  // TODO: use orval generated mock handlers to simplify this file
  // apply all mocks that are auto-generated by @newfold/huapi-js
  getUserHostingMockHandler(hostingList),
  getSiteIpaddressListMockHandler(),
  getSitesSshUserListV2MockHandler(),
  // ...getHostingUAPIMock(),
];

export default handlers;
